import { storeToRefs } from 'pinia'

import { useSegmentTrack } from '~/composables/Segment/useSegmentTrack'

import { useDraftStore } from '~/stores/draft'
import { useInfluencersStore } from '~/stores/influencers'
import { useInfluencersDuplicatesStore } from '~/stores/influencersDuplicates'
import { useInfluencersInteractionsStore } from '~/stores/influencersInteractions'
import { useInfluencersRecommendationsStore } from '~/stores/influencersRecommendations'
import { useTagStore } from '~/stores/tag'
import { useUserStore } from '~/stores/user'
import { useUserBandStore } from '~/stores/userBand'

import type Tag from '~/entities/tag'
import type { StatsV3Influencer } from '~/types/influencer'
import type { StoreInfluencerInteraction } from '~/types/influencerInteraction'
import type { CuratorAddedFrom } from '~/types/Segment/SegmentEventLibrary/SegmentEventLibraryCatalog'

/**
 * Tracks cart additions and deletions and sends metric to segment when needed
 * /!\ Only use this on pages, not on components to avoid duplicate events being sent
 */
export function useSegmentTrackCartAdditions() {
  const { $pinia } = useNuxtApp()
  const route = useRoute()
  const segmentTrack = useSegmentTrack()
  const draftStore = useDraftStore($pinia)
  const { mismatches: INFLUENCER_MISMATCHES } = storeToRefs(
    useInfluencersRecommendationsStore($pinia),
  )
  const { list: DUPLICATE_SUBMISSIONS } = storeToRefs(
    useInfluencersDuplicatesStore($pinia),
  )
  const { id: CURRENT_BAND_ID } = storeToRefs(useUserBandStore($pinia))
  const { id: CURRENT_DRAFT_ID } = storeToRefs(useDraftStore($pinia))
  const { GET_INTERACTION_FROM_INFLUENCER_ID, FETCH: FETCH_INTERACTIONS } =
    useInfluencersInteractionsStore($pinia)
  const { GET_TAGS_FROM_IDS } = useTagStore($pinia)
  const { GET_BY_ID: INFLUENCER_GET_BY_ID } = useInfluencersStore($pinia)
  const { IS_AGENCY } = storeToRefs(useUserStore($pinia))

  const unsubscribeToAddToCartActionMixinData = ref<null | (() => void)>(null)

  function getAddFromIdentifier(
    influencerId: number,
    action: 'add' | 'remove' = 'add',
  ): CuratorAddedFrom | undefined {
    const currentPage = route.path

    if (
      action === 'add' &&
      (Object.entries(INFLUENCER_MISMATCHES.value).some(
        ([key, value]) => influencerId.toString() === key && !!value,
      ) ||
        DUPLICATE_SUBMISSIONS.value.includes(influencerId))
    )
      return 'mismatch-modal'

    if (chainIncludeAssert(currentPage, ['/band/homepage/'])) return 'homepage'

    if (
      chainIncludeAssert(currentPage, [
        '/draft/',
        '/influencers/',
        '/lists/',
      ]) ||
      (chainIncludeAssert(currentPage, ['/influencers/', '/draft/']) &&
        route.query.selectedTab === '1')
    )
      return 'catalog-lists-tab'

    if (chainIncludeAssert(currentPage, ['/agency/favorites/'])) {
      if (route.query.inspectedBucketId?.includes('_'))
        return 'recommended-list'

      return 'lists-dashboard'
    }

    if (chainIncludeAssert(currentPage, ['/influencers/', '/draft/']))
      return 'catalog-curators-tab'

    if (chainIncludeAssert(currentPage, ['/influencer/profile/']))
      return 'curator-profile'

    if (chainIncludeAssert(currentPage, ['/influencer/list/']))
      return 'seo-listing'

    if (
      (chainIncludeAssert(currentPage, ['/draft/']) &&
        route.query.step === '1') ||
      chainIncludeAssert(currentPage, ['/draft/', '/selection/'])
    )
      return 'selection-recap'

    if (
      (chainIncludeAssert(currentPage, ['/draft/']) &&
        route.query.step === '3') ||
      chainIncludeAssert(currentPage, ['/draft/', '/recap/'])
    )
      return 'draft-recap'
  }

  function chainIncludeAssert(string: string, stringSet: string[]): boolean {
    return stringSet.every((e) => string.includes(e))
  }

  onMounted(() => {
    if (IS_AGENCY.value)
      // Lazily fetch the influencer interactions hoping they get here before the first influencer is removed or added to the cart.
      FETCH_INTERACTIONS()

    if (unsubscribeToAddToCartActionMixinData.value) return

    unsubscribeToAddToCartActionMixinData.value = draftStore.$onAction(
      ({ name, args, after }) => {
        if (!['ADD_INF', 'REMOVE_INF'].includes(name)) return

        after(() => {
          const payload =
            typeof args === 'number' ? ([args] as number[]) : (args as number[])

          for (const influencerId of payload) {
            const influencer: StatsV3Influencer | undefined =
              INFLUENCER_GET_BY_ID(influencerId)

            if (!influencer) return

            const influencerInteractions:
              | StoreInfluencerInteraction
              | undefined = GET_INTERACTION_FROM_INFLUENCER_ID(influencer.id)
            const influencerBadgeIds: number[] =
              influencer.tags.identity.influencer_badge ?? []
            const influencerCountryIds: number[] =
              influencer.tags.identity.country ?? []
            const influencerSubgenreIds: number[] =
              influencer.tags.liked?.subgenre ?? []
            const influencerKindIds: number[] =
              influencer.tags.identity.influencer_kind ?? []

            const sharedData = {
              curator_badges_list: (
                GET_TAGS_FROM_IDS(influencerBadgeIds) as Tag[]
              ).map((e) => e.name),
              curator_countries_list: (
                GET_TAGS_FROM_IDS(influencerCountryIds) as Tag[]
              ).map((e) => e.name),
              curator_music_genres_list: (
                GET_TAGS_FROM_IDS(influencerSubgenreIds) as Tag[]
              ).map((e) => e.name),
              curator_name: influencer.entity,
              curator_types_list: (
                GET_TAGS_FROM_IDS(influencerKindIds) as Tag[]
              ).map((e) => e.name),
              id_band: CURRENT_BAND_ID.value,
              id_curator: influencer.id,
              id_draft: CURRENT_DRAFT_ID.value
                ? CURRENT_DRAFT_ID.value
                : undefined,
              num_tracks_sent: influencerInteractions
                ? influencerInteractions.total
                : 0,
            }
            const addFromIdentifier = getAddFromIdentifier(
              influencer.id,
              name === 'ADD_INF' ? 'add' : 'remove',
            )
            if (!addFromIdentifier) return
            if (name === 'ADD_INF') {
              segmentTrack('Catalog - Curator Added to Cart', {
                curator_added_to_cart_from: addFromIdentifier,
                ...sharedData,
              })
            } else {
              segmentTrack('Catalog - Curator Removed from Cart', {
                curator_removed_from: addFromIdentifier,
                ...sharedData,
              })
            }
          }
        })
      },
    )
  })

  onBeforeUnmount(() => {
    if (!unsubscribeToAddToCartActionMixinData.value) return

    unsubscribeToAddToCartActionMixinData.value()
    unsubscribeToAddToCartActionMixinData.value = null
  })
}
